<template>
    <b-modal 
        :visible="visible" centered size="lg" no-close-on-backdrop no-close-on-esc>
        <!-- EN TETE DE LA MODAL -->
        <template #modal-header="{close}">
            <b-col cols="3">
                <!-- BOUTON DE FERMETURE -->
                <b-icon-arrow-left role="button" font-scale="1" @click="visible = false; close();" v-b-tooltip.hover :title="$t('Back')"/> 
            </b-col>
            <b-col class="text-center">
                <!-- TITRE DE LA MODAL -->
                <h6>{{ $t('Modal.Title') }}</h6>
            </b-col>
            <b-col cols="3" class="text-right">
                <!-- BOUTON DE FERMETURE -->
                <b-icon-x-lg role="button" font-scale="1" @click="visible = false; close();" v-b-tooltip.hover :title="$t('Close')" />
            </b-col>
        </template>

        <!-- CORP DE LA MODAL -->
        <template #default>
            <b-row class="py-2 px-3 sticky-top bg-white">
                <!-- INPUT DE RECHERCHE -->
                <b-input-group>
                    <b-form-input 
                    v-model="searchValue" 
                    autofocus 
                    @keydown.enter="executeSearch();"
                    :placeholder="$t('PleaseEnterYourSearch')"/>
                    <template #append>
                        <!-- BOUTON DE VALIDATION DE LA RECHERCHE -->
                        <b-button @click="executeSearch();" :variant="Variant">{{$t('Search')}}</b-button>
                    </template>
                </b-input-group>
            </b-row>

            <!-- ALERTE EN CAS DE RESULTAT NUL -->
            <b-alert v-if="noResult" show variant="danger">{{ $t('NoResult') }}</b-alert>

            <!-- COMPOSANT D'AFFICHAGE SUIVANT STRATEGIE -->
            <component v-if="results.length > 0" :is="strategy.getDisplayer()" @OnRowSelected="setSelection" :Variant="Variant" :Results="results"/>
        </template>

        <!-- PIED DE PAGE DE LA MODAL -->
        <template #modal-footer>
            <!-- BOUTON DE VALIDATION DE LA SELECTION -->
            <b-button @click="validSelection()" :disabled="!(selectedNode && results.length != 0)" :variant="Variant" pill class="ml-auto">{{ $t('Validate') }}</b-button>
        </template>
    </b-modal>
</template>

<script lang="ts">
    /** Composants VUE */
    import {Vue, PropSync, Prop, Watch } from 'vue-property-decorator';
    import Component from 'vue-class-component';
    
    /** Classes et ENUM */
    import ExpendableEnum from '@/enums/ExpendableEnum';
    import SearchStrategyEnum from '@/enums/commons/SearchStrategyEnum';
    import BaseNode from '@/types/BaseNode';
    import Dictionary from '@/types/Dictionary';

    /** Stratégies */ 
    import { SearchStrategy } from './Search/Strategy/SearchStrategy';
    import { SearchStrategyFactory } from './Search/Strategy/SearchStrategyFactory';

    /** Utilitaires */
    import { MAIN_FILTER_TREE } from '@/App.settings';

    /** Traduction */
    import locales from './locales/Search.json';
    
    /** Displayer des stratégie */
    import NodeSearchDisplayer from './Search/Components/NodeSearchDisplayer.vue';
    import ShortMeterSearchDisplayer from './Search/Components/ShortMeterSearchDisplayer.vue';
    import ShortLBOSearchDisplayer from './Search/Components/ShortLBOSearchDisplayer.vue';
    
    import SelectModeEnum from '@/enums/SelectModeEnum';
    import TreeNode from '@/types/TreeNode';
    import { CssClass } from './Generics/GenericTable/GenericTableCssClass';

    /** Affiche une modal de recherche de patrimoine ainsi que les resultats de cette recherche */
    @Component({
        components: {
            ShortMeterSearchDisplayer,
            ShortLBOSearchDisplayer,
            NodeSearchDisplayer
        },
        i18n: { messages: locales }
    })
    export default class Search extends Vue {
        /** Affichage du composant */
        @PropSync('Visible', { default: () => false }) /** type: boolean, */
        public visible!: boolean;

        /** Noeud sélectionné */
        @PropSync('SearchNode', { default: () => {} }) /** type: any, */
        public searchNode!: BaseNode;

        /** Variant de couleur à utiliser sur le tableau de bord */
        @Prop({ default: "primary" })
        public Variant!: string;
        
        /** 
         * Liste des energies attribués à l'objet
         * @see ExpendableEnum 
         */
        @Prop()
        public Expendables!: ExpendableEnum[];

        /** Indique si la recherche est dans le cadre d'un regroupement ou non */
        @Prop({ default: () => false})
        public UseGrouping!: boolean;

        /** 
         * Liste de type d'objet (ObjectTypeEnum sous forme de string) à rechercher
         * @see ObjectTypeEnum dans le projet DCx.ServerSide
         */
        @Prop()
        public TypesList!: string[];

        /**
         * Type de stratégie
         */
        @Prop({ default: () => SearchStrategyEnum })
        public SearchStrategy!: SearchStrategyEnum;

        /**
         * Instance de la stratégie
         */
        private strategy: SearchStrategy = null;

        /** Tableau des résultats */
        private results: Array<BaseNode> = [];
            
        /** Valeur de recherche */
        private searchValue: string = "";

        //region Propriétés pour l'affichage du tableau

        /** Element sélectionné dans le tableau */
        private selectedNode: BaseNode = {} as BaseNode;

        /** Permet d'afficher le message de resultat nul */
        private noResult: boolean = false;

        /** 
         * Validation de la sélection  
         * Remise à 0 du composant
         */
        private async validSelection(){
            this.searchNode = this.selectedNode;
            if (this.FilterTreeStore)
                await this.openNode(this.selectedNode);
            this.visible = false;
            this.results = [];
            this.$emit(this.strategy.getEvent(), this.selectedNode);
        }

        /**
         * Execute la recherche et récupère les resultat
         * @param searchValue valeur à rechercher
         */
        private executeSearch() {
            this.noResult = false;
            // Ecran de chargement
            this.$store.dispatch("ShowLoadingMask", this.$t('SearchInProgress').toString());

            // Inputs de recherche
            const args: Dictionary<any> = {};
            args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "",
            args["SearchValue"] = this.searchValue,
            args["TypesList"] = this.TypesList,
            args["Expendables"] = this.Expendables,
            args["Grouping"] = this.UseGrouping ? this.$store.getters[MAIN_FILTER_TREE + "/GetGrouping"] : null

            console.debug("Search - ExecuteSearch - searchInput", args);

            this.strategy.Execute({"Parameters" : args}).then((data) => {
                if (data) {
                    // On modifie l'objet à la volée lors de l'assignation
                    this.results = data.map((row: BaseNode) => {
                        if (row.Depreciated) {
                            // On ajoute la classe 'text-danger'
                            row.CssClasses.push(new CssClass({ CssClass: 'text-danger' })); 
                        }
                        return row;
                    });

                    this.selectedNode = undefined;

                    if (this.results.length == 1) {
                        this.selectedNode = this.results.at(0);
                        this.validSelection();
                    } else if (this.results.length == 0) {
                        this.noResult = true;
                    }
                }
            }).catch (err => {
                console.error(err);
                this.$bvToast.toast(this.$t('AnErrorOccuredContactAdmin').toString(), {
                title: this.$t('Error').toString(),
                autoHideDelay: 10000,
                variant: "danger",
                solid: true,
                appendToast: true,
                toaster: "b-toaster-top-center"
                });
            }).finally(() => {
                this.$store.dispatch("HideLoadingMask", {})
            });
        }

        /**
         * Mise à jour de la sélection
         * @param row ligne sélectionnée
         */
        private setSelection(row: BaseNode){
            this.selectedNode = row;
        }

        /**
         * Reset du composant
         * Mise à vide du résultat
         */
        public Reset(): void {
            this.results = [];
        }

        /**
         * Si changement de stratégie, on recrée une instance de la stratégie à partir de la factory
         * @param newVal 
         * @param oldVal 
         */
        @Watch('SearchStrategy')
        private onSearchStrategyChanged(newVal: SearchStrategyEnum, oldVal: SearchStrategyEnum) {
            console.debug("Search - onSearchStrategyChanged", newVal, oldVal);
            this.strategy = SearchStrategyFactory.createStrategy(this.SearchStrategy);
        }

        /**
         * Lors du montage du composant
         */
        mounted(){
            this.strategy = SearchStrategyFactory.createStrategy(this.SearchStrategy);
        }

        /** Nom de l'instance de FilterTree liée */
        @Prop()
        public FilterTreeStore!: string;

        /** Ouverture du noeud recherché dans l'arborescence */
        protected async openNode(searchNode: BaseNode): Promise<any> {
            console.debug("Search - openNode() - this.searchNode", searchNode);
            
            let node: TreeNode = this.$store.getters[this.FilterTreeStore + "/Node"](searchNode.Id);

            // Si le noeud a été trouvé dans l'arborescence
            if (node) {
                // Si le noeud est déprécié, on passe en mode historique
                if (node.Depreciated) 
                    await this.$store.dispatch(this.FilterTreeStore + "/SetHistoricMode", true);

                // Ouverture d'un noeud
                await this.$store.dispatch(this.FilterTreeStore + "/OpenNode", { node });

                console.debug("Search - openNode() - SelectMode : ", this.$store.getters[this.FilterTreeStore + "/GetSelectMode"]);
                if (this.$store.getters[this.FilterTreeStore + "/GetSelectMode"] == SelectModeEnum.Single) {	// Sélection du noeud
                    // this.$store.dispatch("SetSingleNode", { newNode: node })
                    await this.$store.dispatch(this.FilterTreeStore + "/SelectNode", { node: node, openNode: true, scrollIntoView: true });
                }
            }
        }

        //#endregion

        /** 
         * Méthode lors de la création du composant
        */
        created() {
            console.debug('Search - created !!!');
        }
    }

</script>

<style scoped>
    /** Pour que la page active de la pagination prenne en compte la couleur de Variant */
    :deep(.page-item.active .page-link) {
		background-color: var(--Variant) !important;
	}

	:deep(.page-link:hover) {
		background-color: var(--VariantLight) !important;
	}
</style>